/* eslint-disable*/
import { useState, useEffect, useRef, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faChevronLeft,
  faChevronRight,
  faRefresh,
  faSearch,
  faSortUp,
  faSortDown,
  faSort,
  faL,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import "bootstrap/dist/css/bootstrap.min.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import { API_URL } from "../config.js";
import { useNavigate, useLocation } from "react-router-dom";
import LoadingSpinner from "../components/LoadingSpinner.jsx";
import moment from "moment";
import { showToast } from "../components/toast.jsx";
import AddFreeServicePopup from "./AddFreeServicePopup.jsx";
import AddPaidServiceOrderPopup from "./AddPaidServiceOrderPopup.jsx";
import BulkEditPopup from "./BulkEditPopup.jsx";
import AddCreditsPopup from "./AddCreditsPopup.jsx";
import SendSmsBulkPopup from "./SendSmsBulkPopup.jsx";
import SendPushNotificationPopup from "./SendPushNotificationPopup.jsx";
import { PermissionWrapper, hasPermission } from "../helpers/permission.js";
import MultiSelectDropdown from "../Subscriber/MultiSelectDropdown";
import "../Subscriber/MultiSelectDropdown.css";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { addMonths, subMonths, startOfMonth } from "date-fns";
import usePersistedState from "../Subscriber/usePersistedState.jsx";
import Swal from "sweetalert2";
import "./subscriptionportfolio.css";
import useDoubleScroll from "../helpers/useDoubleScroll";

const FieldPermissionWrapper = ({ field, children }) => {
  const hasFieldViewPermission = hasPermission(field, "view");
  return hasFieldViewPermission ? children : null;
};

// Helper function to parse single range string into min and max values
const parseNumericRange = (value) => {
  if (!value || typeof value !== "string") return { min: undefined, max: undefined };
  const trimmed = value.trim();
  if (!trimmed) return { min: undefined, max: undefined };

  // Match range like "100-200", "100 - 200", "100 to 200"
  const rangeMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*(?:-|to)\s*(\d+(?:\.\d+)?)$/i);
  if (rangeMatch) {
    return { min: rangeMatch[1], max: rangeMatch[2] };
  }

  // Match ">=100", ">100", "100+"
  const gteMatch = trimmed.match(/^(?:>=?|>)\s*(\d+(?:\.\d+)?)$/) || trimmed.match(/^(\d+(?:\.\d+)?)\+$/);
  if (gteMatch) {
    return { min: gteMatch[1], max: undefined };
  }

  // Match "<=200", "<200"
  const lteMatch = trimmed.match(/^(?:<=?|<)\s*(\d+(?:\.\d+)?)$/);
  if (lteMatch) {
    return { min: undefined, max: lteMatch[1] };
  }

  // Single number "100": set both min and max for exact match
  const singleMatch = trimmed.match(/^(\d+(?:\.\d+)?)$/);
  if (singleMatch) {
    return { min: singleMatch[1], max: singleMatch[1] };
  }

  return { min: undefined, max: undefined };
};

const Subscription = () => {
  const token = localStorage.getItem("authToken");
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const limit = 100;
  const [totalPages, setTotalPages] = useState(1);
  const [isRendering, setIsRendering] = useState(false); // New state for rendering
  const tableRenderedRef = useRef(false); // Ref to track table rendering
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = usePersistedState("subscriptionPortfolio_sortField", ""); // example: 'created_on'
  const [sortOrder, setSortOrder] = usePersistedState("subscriptionPortfolio_sortOrder", ""); // 'asc' or 'desc'
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const activeFilter = queryParams.get("filter") || queryParams.get("type");
  const subscriberId = queryParams.get("subscriberId");
  const prevMonthStart = startOfMonth(subMonths(new Date(), 1));
  const [serviceTypeCounts, setServiceTypeCounts] = useState({});

  const [checkingAll, setCheckingAll] = useState(false);
  const [showSelectAllButton, setShowSelectAllButton] = useState(false);
  const [searchTrigger, setSearchTrigger] = useState(0);

  const handleSearch = () => {
    setPage(1);
    setSearchTrigger((prev) => prev + 1);
  };

  const toggleCheckingAll = () => {
    const newCheckingAll = !checkingAll;
    setCheckingAll(newCheckingAll);

    if (newCheckingAll) {
      // When selecting all records, we keep the currently selected ones
      // The backend will handle selecting all records based on filters
      setShowSelectAllButton(false);
    } else {
      // When deselecting all records, clear the selection
      setSelectedSubscriptions([]);
      setShowSelectAllButton(false);
    }
  };

  const sortBox = (field) => {
    if (sortField === field) {
      const newOrder = sortOrder === "asc" ? "desc" : "asc";
      setSortOrder(newOrder);
      fetchSubscriptions(1); // refetch data on toggle
    } else {
      setSortField(field);
      setSortOrder("asc");
      fetchSubscriptions(1); // refetch data on new field
    }
  };

  const getSortIcon = (field) => {
    if (sortField === field) {
      return sortOrder === "asc" ? faSortUp : faSortDown;
    }
    return faSort; // default icon for inactive columns
  };

  // filters
  const [joinDateFilter, setJoinDateFilter] = usePersistedState(
    "subscriptionPortfolio_joinDateFilter",
    [null, null]
  );
  const [nameFilter, setNameFilter] = usePersistedState(
    "subscriptionPortfolio_nameFilter",
    ""
  );
  const [mobileFilter, setMobileFilter] = usePersistedState(
    "subscriptionPortfolio_mobileFilter",
    ""
  );
  const [leadSourceFilter, setLeadSourceFilter] = usePersistedState(
    "subscriptionPortfolio_leadSourceFilter",
    ""
  );
  const [subscriberCreatedDateFilter, setSubscriberCreatedDateFilter] =
    usePersistedState("subscriptionPortfolio_subscriberCreatedDateFilter", [
      null,
      null,
    ]);
  const [serviceFilter, setServiceFilter] = usePersistedState(
    "subscriptionPortfolio_serviceFilter",
    ""
  );
  const [subscriptionTypeFilter, setSubscriptionTypeFilter] = usePersistedState(
    "subscriptionPortfolio_subscriptionTypeFilter",
    []
  );
  const [serviceStatusFilter, setServiceStatusFilter] = usePersistedState(
    "subscriptionPortfolio_serviceStatusFilter",
    ""
  );
  const [serviceTypeFilter, setServiceTypeFilter] = usePersistedState(
    "subscriptionPortfolio_serviceTypeFilter",
    ""
  );
  const [planTypeFilter, setPlanTypeFilter] = usePersistedState(
    "subscriptionPortfolio_planTypeFilter",
    ""
  );
  const [amountFilter, setAmountFilter] = usePersistedState(
    "subscriptionPortfolio_amountFilter",
    ""
  );
  const [actualAmountFilter, setActualAmountFilter] = usePersistedState(
    "subscriptionPortfolio_actualAmountFilter",
    ""
  );
  const [activtionDateFilter, setActivtionDateFilter] = usePersistedState(
    "subscriptionPortfolio_activtionDateFilter",
    [null, null]
  );
  const [expiryDateFilter, setExpiryDateFilter] = usePersistedState(
    "subscriptionPortfolio_expiryDateFilter",
    [null, null]
  );
  const [renewDateFilter, setRenewDateFilter] = usePersistedState(
    "subscriptionPortfolio_renewDateFilter",
    [null, null]
  );
  const [totalCreditsFilter, setTotalCreditsFilter] = usePersistedState(
    "subscriptionPortfolio_totalCreditsFilter",
    ""
  );
  const [usedCreditsFilter, setUsedCreditsFilter] = usePersistedState(
    "subscriptionPortfolio_usedCreditsFilter",
    ""
  );
  const [availableCreditsFilter, setAvailableCreditsFilter] = usePersistedState(
    "subscriptionPortfolio_availableCreditsFilter",
    ""
  );
  const [salesManagerFilter, setSalesManagerFilter] = usePersistedState(
    "subscriptionPortfolio_salesManagerFilter",
    []
  );
  const [assignedUserFilter, setAssignedUserFilter] = usePersistedState(
    "subscriptionPortfolio_assignedUserFilter",
    ""
  );
  const [updatedByFilter, setUpdatedByFilter] = usePersistedState(
    "subscriptionPortfolio_updatedByFilter",
    ""
  );
  const [utmSourceFilter, setUtmSourceFilter] = usePersistedState(
    "subscriptionPortfolio_utmSourceFilter",
    ""
  );
  const [utmMediumFilter, setUtmMediumFilter] = usePersistedState(
    "subscriptionPortfolio_utmMediumFilter",
    ""
  );
  const [utmCampaignFilter, setUtmCampaignFilter] = usePersistedState(
    "subscriptionPortfolio_utmCampaignFilter",
    ""
  );
  const [utmContentFilter, setUtmContentFilter] = usePersistedState(
    "subscriptionPortfolio_utmContentFilter",
    ""
  );
  const [utmTermFilter, setUtmTermFilter] = usePersistedState(
    "subscriptionPortfolio_utmTermFilter",
    ""
  );
  const [isLoading, setIsLoading] = useState(false);
  // Add selectedSubscriptions state
  const [selectedSubscriptions, setSelectedSubscriptions] = useState([]);
  const [totalSubscriptionAmount, setTotalSubscriptionAmount] = useState(0);
  const [dropdownOptions, setDropdownOptions] = useState({
    leadSourceList: [],
    serviceList: [],
    subscriptionTypeList: [],
    serviceStatusList: [],
    serviceTypeList: [],
    userList: [],
    fullUserList: [],
    salesManagerList: [],
    utmSourceList: [],
    utmMediumList: [],
    utmCampaignList: [],
    utmContentList: [],
    utmTermList: [],
    languageList: [],
    stateList: [],
    paidTradesList: [],
    approvalList: [],
    templatePushNotificationList: [],
    templateSmsList: [],
  });



  const checkRenderingStatus = useCallback(() => {
    if (tableRenderedRef.current) {
      // Add a small delay to ensure the table is fully rendered
      setTimeout(() => {
        setIsRendering(false);
      }, 300);
    }
  }, []);

  // Helper to compare simple arrays (used to avoid unnecessary state updates)
  const arraysEqual = (a, b) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };

  // Helper to compare date ranges [start, end]
  const areDateRangesEqual = (r1, r2) => {
    const [s1, e1] = r1 || [];
    const [s2, e2] = r2 || [];
    const getTime = (d) => (d instanceof Date ? d.getTime() : d || null);
    return getTime(s1) === getTime(s2) && getTime(e1) === getTime(e2);
  };

  // Helper function to build filter params (reusable for all APIs)
  const buildFilterParams = () => {
    const filterParams = {};
    if (joinDateFilter[0]) {
      filterParams.screated_on = moment(joinDateFilter[0]).format("YYYY-MM-DD");
      filterParams.ecreated_on = moment(joinDateFilter[1] || joinDateFilter[0]).format("YYYY-MM-DD");
    }
    if (nameFilter) filterParams.fullname = nameFilter;
    if (mobileFilter) filterParams.mobileno = mobileFilter;
    if (leadSourceFilter) filterParams.lead_source = leadSourceFilter;
    if (subscriberCreatedDateFilter[0]) {
      filterParams.ssubscriber_created_date = moment(
        subscriberCreatedDateFilter[0]
      ).format("YYYY-MM-DD");
      filterParams.esubscriber_created_date = moment(
        subscriberCreatedDateFilter[1] || subscriberCreatedDateFilter[0]
      ).format("YYYY-MM-DD");
    }
    if (serviceFilter) filterParams.serviceid = serviceFilter;
    if (subscriptionTypeFilter && subscriptionTypeFilter.length > 0)
      filterParams.stype = subscriptionTypeFilter.join(",");
    if (serviceStatusFilter) filterParams.status = serviceStatusFilter;
    if (serviceTypeFilter) filterParams.servicetype = serviceTypeFilter;
    if (planTypeFilter) filterParams.plantype = planTypeFilter;
    if (amountFilter) {
      const range = parseNumericRange(amountFilter);
      if (range.min !== undefined) filterParams.min_amount = range.min;
      if (range.max !== undefined) filterParams.max_amount = range.max;
    }
    if (actualAmountFilter) filterParams.actual_amount = actualAmountFilter;
    if (activtionDateFilter[0]) {
      filterParams.sactivation_date = moment(activtionDateFilter[0]).format(
        "YYYY-MM-DD"
      );
      filterParams.eactivation_date = moment(activtionDateFilter[1] || activtionDateFilter[0]).format(
        "YYYY-MM-DD"
      );
    }
    if (expiryDateFilter[0]) {
      filterParams.sexpiry_date = moment(expiryDateFilter[0]).format(
        "YYYY-MM-DD"
      );
      filterParams.eexpiry_date = moment(expiryDateFilter[1] || expiryDateFilter[0]).format(
        "YYYY-MM-DD"
      );
    }
    if (renewDateFilter[0]) {
      filterParams.srenew_date = moment(renewDateFilter[0]).format(
        "YYYY-MM-DD"
      );
      filterParams.erenew_date = moment(renewDateFilter[1] || renewDateFilter[0]).format(
        "YYYY-MM-DD"
      );
    }
    if (totalCreditsFilter) filterParams.trades_total = totalCreditsFilter;
    if (usedCreditsFilter) filterParams.trades_used = usedCreditsFilter;
    if (availableCreditsFilter)
      filterParams.trades_available = availableCreditsFilter;
    if (salesManagerFilter && salesManagerFilter.length > 0)
      filterParams.sales_manager = salesManagerFilter.join(",");
    if (assignedUserFilter) filterParams.assigned_user = assignedUserFilter;
    if (updatedByFilter) filterParams.updated_by = updatedByFilter;
    if (utmSourceFilter) filterParams.utm_source = utmSourceFilter;
    if (utmMediumFilter) filterParams.utm_medium = utmMediumFilter;
    if (utmCampaignFilter) filterParams.utm_campaign = utmCampaignFilter;
    if (utmContentFilter) filterParams.utm_content = utmContentFilter;
    if (utmTermFilter) filterParams.utm_term = utmTermFilter;
    if (sortField) filterParams.orderbycol = sortField;
    if (sortOrder) filterParams.orderby = sortOrder;
    if (activeFilter === "Duplicated") filterParams.duplicated = 1;
    if (subscriberId) filterParams.subscriberid = subscriberId;
    return filterParams;
  };

  const handleRowClick = (id) => {
    if (!hasPermission("subscription", "view")) {
      return; // no navigation without edit permission
    }
    const filterParams = buildFilterParams();

    navigate(`/SubscriptionPortfolio/Edit/${id}`, {
      state: {
        filterParams,
      },
    });
  };

  // API 1: Fetch Subscription List (Priority - show data first)
  // Accept optional pre-built filterParams so the same filters can be reused by stats API
  const fetchSubscriptionList = async (
    pageNumber = 1,
    filterParamsArg = null
  ) => {
    try {
      const startTime = performance.now();
      console.log(
        "⏳ [LIST API] Fetching subscription list at:",
        new Date().toLocaleTimeString()
      );

      // Reuse incoming filterParams if provided, otherwise build from current state
      const filterParams = filterParamsArg || buildFilterParams();

      const response = await axios.post(
        `${API_URL}subscription/subscriptionPortfolio?limit=${limit}&page=${pageNumber}`,
        filterParams,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const apiEndTime = performance.now();
      console.log(
        "✅ [LIST API] Response received at:",
        new Date().toLocaleTimeString()
      );
      console.log(
        "⏱️ [LIST API DURATION] Time taken:",
        (apiEndTime - startTime).toFixed(2),
        "ms"
      );

      if (response.data.success && response.data.data) {
        const { subscriptions } = response.data.data;
        const subscriptionsArray = Array.isArray(subscriptions)
          ? subscriptions
          : [];

        setUsers(subscriptionsArray);
        setFilteredUsers(subscriptionsArray);

        // Mark table as rendered
        requestAnimationFrame(() => {
          tableRenderedRef.current = true;
          checkRenderingStatus();
        });
      }
    } catch (error) {
      console.error("Error fetching subscription list:", error);
      setError(error.message);
      setUsers([]);
      setFilteredUsers([]);
      tableRenderedRef.current = true;
      checkRenderingStatus();
      showToast.error("Failed to fetch subscription list: " + error.message);
    }
  };

  // API 2: Fetch Subscription Stats
  // Accept optional pre-built filterParams so the same filters used for list are used here
  const fetchSubscriptionStats = async (filterParamsArg = null) => {
    try {
      const startTime = performance.now();
      console.log(
        "⏳ [STATS API] Fetching subscription stats at:",
        new Date().toLocaleTimeString()
      );

      // Reuse incoming filterParams if provided, otherwise build from current state
      const filterParams = filterParamsArg || buildFilterParams();

      const response = await axios.post(
        `${API_URL}subscription/subscriptionPortfolio/stats`,
        filterParams,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const apiEndTime = performance.now();
      console.log(
        "✅ [STATS API] Response received at:",
        new Date().toLocaleTimeString()
      );
      console.log(
        "⏱️ [STATS API DURATION] Time taken:",
        (apiEndTime - startTime).toFixed(2),
        "ms"
      );

      if (response.data.success && response.data.data) {
        const { filteredCount, subscription_amount, serviceTypeCounts } =
          response.data.data;
        setTotalRecords(filteredCount || 0);
        setTotalSubscriptionAmount(subscription_amount || 0);
        setServiceTypeCounts(serviceTypeCounts || {});

        // Calculate total pages based on filteredCount
        const totalPages = Math.ceil((filteredCount || 0) / limit);
        setTotalPages(totalPages);
      }
    } catch (error) {
      console.error("Error fetching subscription stats:", error);
      showToast.error("Failed to fetch subscription stats: " + error.message);
    }
  };

  // API 3: Fetch Dropdown Options
  const fetchDropdownOptions = async () => {
    try {
      const startTime = performance.now();
      console.log(
        "⏳ [DROPDOWN API] Fetching dropdown options at:",
        new Date().toLocaleTimeString()
      );

      const response = await axios.get(`${API_URL}subscription/get-dropdown`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const apiEndTime = performance.now();
      console.log(
        "✅ [DROPDOWN API] Response received at:",
        new Date().toLocaleTimeString()
      );
      console.log(
        "⏱️ [DROPDOWN API DURATION] Time taken:",
        (apiEndTime - startTime).toFixed(2),
        "ms"
      );

      if (response.data.success && response.data.data) {
        setDropdownOptions({
          leadSourceList: response.data.data.lead_sourcelist || [],
          serviceList: response.data.data.PortfolioServicelist || [],
          subscriptionTypeList: response.data.data.subscription_typelist || [],
          serviceStatusList: response.data.data.service_statuslist || [],
          serviceTypeList: response.data.data.service_typelist || [],
          userList: response.data.data.salesManagerlist || [],
          fullUserList: response.data.data.fulluserlist || [],
          salesManagerList: response.data.data.salesManagerlist || [],
          utmSourceList: response.data.data.utmsourcelist || [],
          utmMediumList: response.data.data.utmmediumlist || [],
          utmCampaignList: response.data.data.utmcampaignlist || [],
          utmContentList: response.data.data.utmcontentlist || [],
          utmTermList: response.data.data.utmtermlist || [],
          languageList: response.data.data.languagelist || [],
          stateList: response.data.data.statelist || [],
          paidTradesList: response.data.data.paidtradelist || [],
          approvalList: response.data.data.approvallist || [],
          templatePushNotificationList:
            response.data.data.templatePushNotificationlist || [],
          templateSmsList: response.data.data.templateSmslist || [],
          distributorList: response.data.data.distributorList || [],
        });
      }
    } catch (error) {
      console.error("Error fetching dropdown options:", error);
    }
  };

  // Listen for clear filter event from Sidebar
  useEffect(() => {
    const handleClearFilters = () => {
      setJoinDateFilter([null, null]);
      setNameFilter("");
      setMobileFilter("");
      setLeadSourceFilter("");
      setSubscriberCreatedDateFilter([null, null]);
      setServiceFilter("");
      setSubscriptionTypeFilter([]);
      setServiceStatusFilter("");
      setServiceTypeFilter("");
      setPlanTypeFilter("");
      setAmountFilter("");
      setActualAmountFilter("");
      setActivtionDateFilter([null, null]);
      setExpiryDateFilter([null, null]);
      setRenewDateFilter([null, null]);
      setTotalCreditsFilter("");
      setUsedCreditsFilter("");
      setAvailableCreditsFilter("");
      setSalesManagerFilter([]);
      setAssignedUserFilter("");
      setUpdatedByFilter("");
      setUtmSourceFilter("");
      setUtmMediumFilter("");
      setUtmCampaignFilter("");
      setUtmContentFilter("");
      setUtmTermFilter("");
      setPage(1);
    };

    window.addEventListener('clearSubscriptionPortfolioFiltersEvent', handleClearFilters);
    return () => {
      window.removeEventListener('clearSubscriptionPortfolioFiltersEvent', handleClearFilters);
    };
  }, []);

  // Fetch dropdown options once on component mount
  useEffect(() => {
    fetchDropdownOptions();
  }, []);

  // Main function to fetch all data in sequence: List -> Stats -> Dropdown
  // const fetchSubscriptions = async (pageNumber = 1) => {
  //   try {
  //     setLoading(true);
  //     setIsRendering(true);
  //     tableRenderedRef.current = false;

  //     const overallStartTime = performance.now();
  //     console.log(
  //       "🚀 [FETCH SUBSCRIPTIONS START] Starting data fetch at:",
  //       new Date().toLocaleTimeString()
  //     );

  //     // Build filter params ONCE and reuse for list + stats to keep them in sync
  //     const filterParams = buildFilterParams();

  //     // Step 1: Fetch List (Priority - show data first, loading spinner shows here)
  //     await fetchSubscriptionList(pageNumber, filterParams);

  //     // Step 2: Fetch Stats (can load in background, no spinner)
  //     await fetchSubscriptionStats(filterParams);

  //     setLoading(false);

  //     // Step 3: Fetch Dropdown Options (can load in background, no spinner)
  //     await fetchDropdownOptions();

  //     const overallEndTime = performance.now();
  //     console.log(
  //       "🎉 [FETCH SUBSCRIPTIONS COMPLETE] All data loaded at:",
  //       new Date().toLocaleTimeString()
  //     );
  //     console.log(
  //       "⏳ [TOTAL TIME] All APIs took:",
  //       (overallEndTime - overallStartTime).toFixed(2),
  //       "ms"
  //     );
  //   } catch (error) {
  //     console.error("Error in fetchSubscriptions:", error);
  //     setError(error.message);
  //     setLoading(false);
  //   }
  // };
  const fetchSubscriptions = async (pageNumber = 1) => {
    try {
      setLoading(true);
      setIsRendering(true);
      tableRenderedRef.current = false;

      const overallStartTime = performance.now();
      console.log(
        "🚀 [FETCH SUBSCRIPTIONS START] Starting data fetch at:",
        new Date().toLocaleTimeString()
      );

      // ✅ Build filter params ONCE
      const filterParams = buildFilterParams();

      // ✅ STEP 1: Fetch MAIN TABLE DATA (blocking)
      await fetchSubscriptionList(pageNumber, filterParams);

      // ✅ STOP TABLE SKELETON AS SOON AS LIST IS READY
      setIsRendering(false);

      // ✅ STEP 2 + 3: Background APIs (NON-BLOCKING)
      Promise.allSettled([
        fetchSubscriptionStats(filterParams),
      ]).then((results) => {
        console.log("📦 Background APIs completed:", {
          stats: results[0].status,
        });
      });

      const overallEndTime = performance.now();
      console.log(
        "⏳ [LIST LOADED] Time:",
        (overallEndTime - overallStartTime).toFixed(2),
        "ms"
      );
    } catch (error) {
      console.error("Error in fetchSubscriptions:", error);
      setError(error.message);
    } finally {
      // ❌ Do NOT block table rendering here
      setLoading(false);
    }
  };


  useEffect(() => {
    if (filteredUsers.length > 0) {
      checkRenderingStatus();
    }
  }, [filteredUsers, checkRenderingStatus]);

  useEffect(() => {
    fetchSubscriptions(page);
  }, [
    page,
    // joinDateFilter,
    // nameFilter,
    // mobileFilter,
    leadSourceFilter,
    // subscriberCreatedDateFilter,
    serviceFilter,
    subscriptionTypeFilter,
    serviceStatusFilter,
    serviceTypeFilter,
    planTypeFilter,
    // amountFilter,
    // actualAmountFilter,
    // activtionDateFilter,
    // expiryDateFilter,
    // renewDateFilter,
    totalCreditsFilter,
    // usedCreditsFilter,
    // availableCreditsFilter,
    salesManagerFilter,
    assignedUserFilter,
    updatedByFilter,
    utmSourceFilter,
    utmMediumFilter,
    utmCampaignFilter,
    utmContentFilter,
    utmTermFilter,
    sortField,
    sortOrder,
    // activeFilter,
    subscriberId,
    searchTrigger,
  ]);

  // fetchDropdownOptions is now called from fetchSubscriptions to maintain proper loading sequence
  // Removed separate useEffect to avoid duplicate calls

  useEffect(() => {
    const today = new Date();
    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const endOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59,
      999
    );

    const findTypeIdByName = (name) => {
      const found = dropdownOptions.subscriptionTypeList.find(
        (i) => (i.pick_name || "").toLowerCase() === (name || "").toLowerCase()
      );
      return found ? String(found.pick_name) : "";
    };

    const findStatusIdByName = (name) => {
      const found = dropdownOptions.serviceStatusList.find(
        (i) => (i.pick_name || "").toLowerCase() === (name || "").toLowerCase()
      );
      return found ? String(found.pick_name) : "";
    };

    const findServiceTypeIdByName = (name) => {
      const found = dropdownOptions.serviceTypeList.find(
        (i) => (i.pick_name || "").toLowerCase() === (name || "").toLowerCase()
      );
      return found ? String(found.pick_name) : "";
    };

    // Clear ALL page filters to avoid conflicts with Sidebar filters
    const clearPageFilters = () => {
      setJoinDateFilter([null, null]);
      setNameFilter("");
      setMobileFilter("");
      setLeadSourceFilter("");
      setSubscriberCreatedDateFilter([null, null]);
      setServiceFilter("");
      setSubscriptionTypeFilter([]);
      setServiceStatusFilter("");
      setServiceTypeFilter("");
      setPlanTypeFilter("");
      setAmountFilter("");
      setActualAmountFilter("");
      setActivtionDateFilter([null, null]);
      setExpiryDateFilter([null, null]);
      setRenewDateFilter([null, null]);
      setTotalCreditsFilter("");
      setUsedCreditsFilter("");
      setAvailableCreditsFilter("");
      setSalesManagerFilter([]);
      setAssignedUserFilter("");
      setUpdatedByFilter("");
      setUtmSourceFilter("");
      setUtmMediumFilter("");
      setUtmCampaignFilter("");
      setUtmContentFilter("");
      setUtmTermFilter("");
      setSortField("");
      setSortOrder("");
      setPage(1);
    };

    setPage(1);

    // If no active filter or it's the main Portfolios page, clear all page filters
    if (!activeFilter || activeFilter === "SubscriptionPortfolio") {
      // clearPageFilters();
      fetchSubscriptions(1);
      return;
    }

    // Clear previous quick filters before applying new ones
    clearPageFilters();

    switch (activeFilter) {
      case "Free": {
        setSubscriptionTypeFilter(
          findTypeIdByName("Free") ? [findTypeIdByName("Free")] : []
        );
        break;
      }
      case "FreeActive": {
        setSubscriptionTypeFilter(
          findTypeIdByName("Free") ? [findTypeIdByName("Free")] : []
        );
        setServiceStatusFilter(findStatusIdByName("Active"));
        break;
      }
      case "ExpiredToday": {
        setExpiryDateFilter([startOfToday, endOfToday]);
        break;
      }
      case "FreeExpired": {
        setSubscriptionTypeFilter(
          findTypeIdByName("Free") ? [findTypeIdByName("Free")] : []
        );
        setServiceStatusFilter(findStatusIdByName("Expired"));
        break;
      }
      case "Paid": {
        const paidTypes = ["Paid", "Paid F", "Paid P"]
          .map((t) => findTypeIdByName(t) || t)
          .filter(Boolean);

        setSubscriptionTypeFilter(paidTypes);
        break;
      }
      case "PaidActive": {
        const paidTypes = ["Paid", "Paid F", "Paid P"]
          .map((t) => findTypeIdByName(t) || t)
          .filter(Boolean);

        setSubscriptionTypeFilter(paidTypes);
        setServiceStatusFilter(findStatusIdByName("Active"));
        break;
      }
      case "PaidInProgress": {
        const paidTypes = ["Paid", "Paid F", "Paid P"]
          .map((t) => findTypeIdByName(t) || t)
          .filter(Boolean);

        setSubscriptionTypeFilter(paidTypes);
        setServiceStatusFilter(findStatusIdByName("In Progress"));
        break;
      }
      case "PaidExpired": {
        const paidTypes = ["Paid", "Paid F", "Paid P"]
          .map((t) => findTypeIdByName(t) || t)
          .filter(Boolean);

        setSubscriptionTypeFilter(paidTypes);
        setServiceStatusFilter(findStatusIdByName("Expired"));
        break;
      }
      case "Renewed": {
        setServiceTypeFilter(findServiceTypeIdByName("Renewal"));
        break;
      }
      case "Duplicated": {
        // Handled via API flag above
        break;
      }
      default:
        break;
    }
  }, [activeFilter, dropdownOptions]);
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, totalRecords);

  const [isModalOpenPaidServiceOrder, setIsModalOpenPaidServiceOrder] =
    useState(false);
  const [isModalOpenFreeService, setIsModalOpenFreeService] = useState(false);
  const [isModalOpenBulkEdit, setIsModalOpenBulkEdit] = useState(false);
  const [isModalOpenCreditsAdd, setIsModalOpenCreditsAdd] = useState(false);
  const [isModalOpenSendSms, setIsModalOpenSendSms] = useState(false);
  const [isModalOpenSendPush, setIsModalOpenSendPush] = useState(false);
  const [isModalOpenSendEmail, setIsModalOpenSendEmail] = useState(false);
  const [emailData, setEmailData] = useState({
    emailtemp: "",
    subject: "",
    message: "",
    file: null,
  });
  const [templateList, setTemplateList] = useState([]);

  const handleTemplateChange = async (templateId) => {
    if (!templateId) {
      setEmailData((prev) => ({
        ...prev,
        subject: "",
        message: "",
      }));
      return;
    }
    // Always update selected template ID immediately
    setEmailData((prev) => ({
      ...prev,
      emailtemp: templateId,
    }));

    if (!templateId) {
      setEmailData((prev) => ({
        ...prev,
        subject: "",
        message: "",
      }));
      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}settings/template/email/${templateId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        const template = response.data.data;
        setEmailData((prev) => ({
          ...prev,
          subject: template.subject || "",
          message: template.message || "",
        }));
      }
    } catch (error) {
      console.error("Error fetching template:", error);
      showToast.error("Failed to load template");
    }
  };

  function hasAnyFilter() {
    return (
      (joinDateFilter[0] || joinDateFilter[1]) ||
      nameFilter ||
      mobileFilter ||
      leadSourceFilter ||
      (subscriberCreatedDateFilter[0] || subscriberCreatedDateFilter[1]) ||
      serviceFilter ||
      (subscriptionTypeFilter && subscriptionTypeFilter.length > 0) ||
      serviceStatusFilter ||
      serviceTypeFilter ||
      planTypeFilter ||
      amountFilter ||
      actualAmountFilter ||
      (activtionDateFilter[0] || activtionDateFilter[1]) ||
      (expiryDateFilter[0] || expiryDateFilter[1]) ||
      (renewDateFilter[0] || renewDateFilter[1]) ||
      totalCreditsFilter ||
      usedCreditsFilter ||
      availableCreditsFilter ||
      (salesManagerFilter && salesManagerFilter.length > 0) ||
      assignedUserFilter ||
      updatedByFilter ||
      utmSourceFilter ||
      utmMediumFilter ||
      utmCampaignFilter ||
      utmContentFilter ||
      utmTermFilter ||
      sortField ||
      sortOrder
    );
  }
  function getCurrentFilterParams() {
    const filterParams = {};
    if (joinDateFilter[0]) {
      filterParams.screated_on = moment(joinDateFilter[0]).format("YYYY-MM-DD");
      filterParams.ecreated_on = moment(joinDateFilter[1] || joinDateFilter[0]).format("YYYY-MM-DD");
    }
    if (nameFilter) filterParams.fullname = nameFilter;
    if (mobileFilter) filterParams.mobileno = mobileFilter;
    if (leadSourceFilter) filterParams.lead_source = leadSourceFilter;
    if (subscriberCreatedDateFilter[0]) {
      filterParams.ssubscriber_created_date = moment(
        subscriberCreatedDateFilter[0]
      ).format("YYYY-MM-DD");
      filterParams.esubscriber_created_date = moment(
        subscriberCreatedDateFilter[1] || subscriberCreatedDateFilter[0]
      ).format("YYYY-MM-DD");
    }
    if (serviceFilter) filterParams.serviceid = serviceFilter;
    if (subscriptionTypeFilter) filterParams.stype = subscriptionTypeFilter;
    if (serviceStatusFilter) filterParams.status = serviceStatusFilter;
    if (serviceTypeFilter) filterParams.servicetype = serviceTypeFilter;
    if (planTypeFilter) filterParams.plantype = planTypeFilter;
    if (amountFilter) {
      const range = parseNumericRange(amountFilter);
      if (range.min !== undefined) filterParams.min_amount = range.min;
      if (range.max !== undefined) filterParams.max_amount = range.max;
    }
    if (actualAmountFilter) filterParams.actual_amount = actualAmountFilter;
    if (activtionDateFilter[0]) {
      filterParams.sactivation_date = moment(activtionDateFilter[0]).format(
        "YYYY-MM-DD"
      );
      filterParams.eactivation_date = moment(activtionDateFilter[1] || activtionDateFilter[0]).format(
        "YYYY-MM-DD"
      );
    }
    if (expiryDateFilter[0]) {
      filterParams.sexpiry_date = moment(expiryDateFilter[0]).format(
        "YYYY-MM-DD"
      );
      filterParams.eexpiry_date = moment(expiryDateFilter[1] || expiryDateFilter[0]).format(
        "YYYY-MM-DD"
      );
    }
    if (renewDateFilter[0]) {
      filterParams.srenew_date = moment(renewDateFilter[0]).format(
        "YYYY-MM-DD"
      );
      filterParams.erenew_date = moment(renewDateFilter[1] || renewDateFilter[0]).format(
        "YYYY-MM-DD"
      );
    }
    if (totalCreditsFilter) filterParams.trades_total = totalCreditsFilter;
    if (usedCreditsFilter) filterParams.trades_used = usedCreditsFilter;
    if (availableCreditsFilter)
      filterParams.trades_available = availableCreditsFilter;
    if (salesManagerFilter) filterParams.sales_manager = salesManagerFilter;
    if (assignedUserFilter) filterParams.assigned_user = assignedUserFilter;
    if (updatedByFilter) filterParams.updated_by = updatedByFilter;
    if (utmSourceFilter) filterParams.utm_source = utmSourceFilter;
    if (utmMediumFilter) filterParams.utm_medium = utmMediumFilter;
    if (utmCampaignFilter) filterParams.utm_campaign = utmCampaignFilter;
    if (utmContentFilter) filterParams.utm_content = utmContentFilter;
    if (utmTermFilter) filterParams.utm_term = utmTermFilter;
    if (sortField) filterParams.orderbycol = sortField;
    if (sortOrder) filterParams.orderby = sortOrder;
    return filterParams;
  }
  // const handleEmailSubmit = async (e) => {
  //   e.preventDefault();

  //   if (!emailData.subject || !emailData.message) {
  //     showToast.warning("Please fill in all required fields");
  //     return;
  //   }

  //   const filterParams = {};
  //   // Add all your existing filter params here as in other handlers

  //   try {
  //     setLoading(true);
  //     const formData = new FormData();

  //     // Required fields as per API
  //     const selectedSubscriberIds = users
  //       .filter((u) => selectedSubscriptions.includes(u.id))
  //       .map((u) => u.subscriberid)
  //       .filter(Boolean);
  //     formData.append(
  //       "selectedIds",
  //       selectedSubscriberIds.length > 0
  //         ? selectedSubscriberIds.join(",")
  //         : selectedSubscriptions.join(",")
  //     );
  //     formData.append("filters", JSON.stringify(filterParams) || "{}");
  //     formData.append("templateId", emailData.emailtemp || "");

  //     // Email details
  //     formData.append("subject", emailData.subject);
  //     formData.append("emailmessage", emailData.message);

  //     if (emailData.file) {
  //       formData.append("imageUrl", emailData.file);
  //     }

  //     // 👇 API call with checkingAll as query param
  //     const response = await axios.post(
  //       `${API_URL}subscription/email-bulk-popup?checkingAll=false`,
  //       formData,
  //       {
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           "Content-Type": "multipart/form-data",
  //         },
  //       }
  //     );

  //     if (response.status === 200 || response.status === 201) {
  //       showToast.success("Emails sent successfully");
  //       setIsModalOpenSendEmail(false);
  //       setSelectedSubscriptions([]);
  //       setEmailData({
  //         emailtemp: "",
  //         subject: "",
  //         message: "",
  //         file: null,
  //       });
  //     } else {
  //       showToast.error("Failed to send emails");
  //     }
  //   } catch (error) {
  //     console.error("Error sending emails:", error);
  //     showToast.error("An error occurred while sending emails");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    if (!emailData.subject || !emailData.message) {
      showToast.warning("Please fill in all required fields");
      return;
    }

    // Use the helpers above
    const filterParams = checkingAll
      ? hasAnyFilter()
        ? getCurrentFilterParams()
        : {}
      : {};

    try {
      setLoading(true);
      const formData = new FormData();

      // IDs of selected subscriptions or empty if checkingAll
      formData.append(
        "selectedIds",
        checkingAll ? "" : selectedSubscriptions.join(",")
      );
      formData.append("filters", JSON.stringify(filterParams));
      formData.append("templateId", emailData.emailtemp || "");
      formData.append("subject", emailData.subject);
      formData.append("emailmessage", emailData.message);

      if (emailData.file) {
        formData.append("imageUrl", emailData.file);
      }

      const response = await axios.post(
        `${API_URL}subscription/email-bulk-popup?checkingAll=${checkingAll}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        showToast.success("Emails sent successfully");
        setIsModalOpenSendEmail(false);
        setSelectedSubscriptions([]);
        setEmailData({
          emailtemp: "",
          subject: "",
          message: "",
          file: null,
        });
      } else {
        showToast.error("Failed to send emails");
      }
    } catch (error) {
      console.error("Error sending emails:", error);
      showToast.error("An error occurred while sending emails");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchEmailTemplates = async () => {
      try {
        const response = await axios.get(
          `${API_URL}settings/template/email/list`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log("fetchEmailTemplates", response);

        // Access the data array directly from response.data.data
        const templates = response.data.data;
        if (Array.isArray(templates)) {
          setTemplateList(templates);
        } else {
          setTemplateList([]);
        }
      } catch (error) {
        console.error("Error fetching email templates:", error);
      }
    };

    fetchEmailTemplates();
  }, []);

  // Close the modal
  const closeModal = () => {
    setIsModalOpenPaidServiceOrder(false);
    setIsModalOpenFreeService(false);
    setIsModalOpenBulkEdit(false);
    setIsModalOpenCreditsAdd(false);
    setIsModalOpenSendSms(false);
    setIsModalOpenSendPush(false);
  };

  // Toggle the modal open/close
  const toggleModalPaidServiceOrder = () => {
    setIsModalOpenPaidServiceOrder(!isModalOpenPaidServiceOrder);
  };
  const toggleModalFreeService = () => {
    setIsModalOpenFreeService(!isModalOpenFreeService);
  };

  // Handle date picker change
  const handleJoinDateChange = (dates) => {
    setJoinDateFilter(dates);
  };
  const handleSubscriberDateChange = (dates) => {
    setSubscriberCreatedDateFilter(dates);
  };
  const handleActivationDateChange = (dates) => {
    setActivtionDateFilter(dates);
  };
  const handleExpiryDateChange = (dates) => {
    setExpiryDateFilter(dates);
  };
  const handleRenewDateChange = (dates) => {
    setRenewDateFilter(dates);
  };

  const handleRefresh = () => {
    fetchSubscriptions(1);
  };

  const handleCheckboxChange = (id, checked) => {
    if (checked) {
      setSelectedSubscriptions((prev) => [...prev, id]);
    } else {
      setSelectedSubscriptions((prev) => prev.filter((subId) => subId !== id));
      // If unchecking and checkingAll was true, set it to false
      if (checkingAll) {
        setCheckingAll(false);
      }
    }
  };

  // Check if we should show "Select All Records" button
  useEffect(() => {
    // Show the button when all visible records are selected but total count is greater
    const allVisibleSelected =
      filteredUsers.length > 0 &&
      selectedSubscriptions.length === filteredUsers.length &&
      totalRecords > filteredUsers.length &&
      !checkingAll;
    setShowSelectAllButton(allVisibleSelected);
  }, [selectedSubscriptions, filteredUsers, totalRecords, checkingAll]);

  const handleActionChange = async (action) => {
    if (action === "delete") {
      if (selectedSubscriptions.length === 0 && !checkingAll) {
        showToast.warning("Please select at least one subscription to delete");
        return;
      }
      if (!hasPermission("subscription", "delete")) {
        showToast.warning("You don't have permission to delete subscriptions");
        return;
      }

      const result = await Swal.fire({
        title: "Are you sure?",
        text: `You are about to delete ${selectedSubscriptions.length} subscription(s). This action cannot be undone!`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete them!",
        cancelButtonText: "No, cancel",
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
      });

      if (result.isConfirmed) {
        try {
          const filterParams = buildFilterParams();

          const payload = {
            checkingAll: checkingAll ? 1 : 0,
            filter: filterParams,
            del: selectedSubscriptions,
          };

          const response = await axios.post(
            `${API_URL}subscription/delete`,
            payload,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (response.data.success === true) {
            showToast.success(
              response.data.message || "Subscriptions deleted successfully"
            );
            setSelectedSubscriptions([]);
            fetchSubscriptions();
          }
        } catch (error) {
          console.error("Error deleting subscriptions:", error);
          console.log(error.response?.data || "Unknown error");
          showToast.error("An error occurred while deleting subscriptions");
        }
      }
    } else if (action === "bulkExport") {
      if (selectedSubscriptions.length === 0 && !checkingAll) {
        showToast.warning("Please select at least one subscription to export");
        return;
      }

      handleBulkExport(selectedSubscriptions);
    } else if (action === "bulkEdit") {
      if (!hasPermission("subscription", "edit")) {
        showToast.warning("You don't have permission to edit orders");
        return;
      }
      if (selectedSubscriptions.length === 0 && !checkingAll) {
        showToast.warning("Please select at least one subscription to edit");
        return;
      }

      setIsModalOpenBulkEdit(true);
    } else if (action === "addCredits") {
      setIsModalOpenCreditsAdd(true);
    } else if (action === "sendSms") {
      if (selectedSubscriptions.length === 0 && !checkingAll) {
        showToast.warning(
          "Please select at least one subscription to send sms"
        );
        return;
      }

      setIsModalOpenSendSms(true);
    } else if (action === "sendPush") {
      if (selectedSubscriptions.length === 0 && !checkingAll) {
        showToast.warning(
          "Please select at least one subscription to send push notification"
        );
        return;
      }

      setIsModalOpenSendPush(true);
    } else if (action === "sendEmail") {
      if (selectedSubscriptions.length === 0 && !checkingAll) {
        showToast.warning(
          "Please select at least one subscription to send email"
        );
        return;
      }
      // Reset email fields when opening the modal
      setEmailData({
        emailtemp: "",
        subject: "",
        message: "",
        file: null,
      });
      setIsModalOpenSendEmail(true);
    } else if (action === "campaignExport") {
      if (selectedSubscriptions.length === 0 && !checkingAll) {
        showToast.warning("Please select at least one subscription to export");
        return;
      }
      handleCampaignExport(selectedSubscriptions);
    }
  };

  const handleBulkEditSubmit = async (bulkEditData) => {
    if (selectedSubscriptions.length === 0 && !checkingAll) {
      showToast.warning("No subscription selected for update");
      return;
    }

    // Filter out empty fields to only update fields that have been changed
    const updateData = {};
    Object.keys(bulkEditData).forEach((key) => {
      if (bulkEditData[key] !== "") {
        // Convert ID to actual value based on the field type
        let value = bulkEditData[key];

        switch (key) {
          case "serviceid":
            const serviceItem = dropdownOptions.serviceList.find(
              (item) => item.id == value
            );
            value = serviceItem ? serviceItem.id : value;
            break;

          case "status":
            const statusItem = dropdownOptions.serviceStatusList.find(
              (item) => item.pick_name == value
            );
            value = statusItem ? statusItem.pick_name : value;
            break;

          case "stype":
            const stypeItem = dropdownOptions.subscriptionTypeList.find(
              (item) => item.pick_name == value
            );
            value = stypeItem ? stypeItem.pick_name : value;
            break;

          case "servicetype":
            const serviceTypeItem = dropdownOptions.serviceTypeList.find(
              (item) => item.pick_name == value
            );
            value = serviceTypeItem ? serviceTypeItem.pick_name : value;
            break;

          case "order_approval":
            const approvalItem = dropdownOptions.approvalList.find(
              (item) => item.pick_name == value
            );
            value = approvalItem ? approvalItem.pick_name : value;
            break;

          case "amount":
            const amount = value;
            value = amount ? Number(value) : Number(value);
            break;

          case "pay_date":
            const payDate = value;
            value = payDate ? value : value;
            break;

          case "pay_mode":
            const payMode = value;
            value = payMode ? value : value;
            break;

          case "pay_refno":
            const payRef = value;
            value = payRef ? value : value;
            break;

          case "sales_manager":
            const salesManager = dropdownOptions.salesManagerList.find(
              (item) => item.id == value
            );
            value = salesManager ? salesManager.id : value;
            break;

          case "activation_date":
            const activationDate = value;
            value = activationDate ? value : value;
            break;

          case "expiry_date":
            const expiryDate = value;
            value = expiryDate ? value : value;
            break;
        }

        updateData[key] = value;
      }
    });

    if (Object.keys(updateData).length === 0) {
      alert("No changes to update");
      return;
    }

    try {
      setIsLoading(true);

      const filterParams = buildFilterParams();

      const payload = {
        checkingAll: checkingAll ? 1 : 0,
        subscriptionIds: selectedSubscriptions,
        filter: filterParams,
        ...updateData,
      };

      console.log("Sending payload:", payload);

      const response = await axios.post(
        `${API_URL}subscription/bulk-update`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        showToast.success(
          response.data.message || "Subscribers updated successfully"
        );
        fetchSubscriptions();

        // Close the modal after a delay
        setTimeout(() => {
          // Clear selected subscribers after successful update
          setSelectedSubscriptions([]);
        }, 2000);
      } else {
        showToast.error(
          response.data.message || "Failed to update subscribers"
        );
      }
    } catch (error) {
      console.error("Error updating subscribers:", error);

      if (error.response) {
        console.log("Error response data:", error.response.data);
        console.log("Error response status:", error.response.status);
        console.log("Error response headers:", error.response.headers);

        showToast.error(error.response.data.message || "Server error occurred");
      } else if (error.request) {
        console.log("Error request:", error.request);
        showToast.error("No response received from server");
      } else {
        console.log("Error message:", error.message);
        showToast.error("Error setting up request: " + error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };
  const handleBulkExport = async (selectedSubscriptions) => {
    const filterParams = buildFilterParams();

    try {
      const response = await axios.post(
        `${API_URL}subscription/export`,
        { selectedIds: selectedSubscriptions, filters: filterParams },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
          params: {
            checkAll: checkingAll,
          },
          responseType: "blob",
        }
      );

      if (response.status == 201) {
        const now = new Date();
        const formattedDate = now
          .toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          })
          .replace(/[/, ]+/g, "_"); // Replace separators with underscores

        const fileName = `SubscriptionList-${formattedDate}.xlsx`;

        // ✅ Create a downloadable link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.log(error.response?.data || "Unknown error");
      showToast.error("An error occurred while export subscribers");
    }
  };
  const handleCampaignExport = async (selectedSubscriptions) => {
    const filterParams = buildFilterParams();

    // ✅ Remove empty arrays, nulls, or undefined values
    Object.keys(filterParams).forEach((key) => {
      const val = filterParams[key];
      if (
        val === undefined ||
        val === null ||
        (Array.isArray(val) && val.length === 0)
      ) {
        delete filterParams[key];
      }
    });

    // ✅ If no valid filters, send empty object
    const finalFilters =
      Object.keys(filterParams).length > 0 ? filterParams : {};

    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}subscription/campaign-export`,
        {
          selectedIds: selectedSubscriptions,
          filters: finalFilters,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
          params: { checkAll: checkingAll },
          responseType: "blob",
        }
      );

      if (response.status === 201) {
        const now = new Date();
        const formattedDate = now
          .toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          })
          .replace(/[/, ]+/g, "_");

        const fileName = `SubscriptionList-${formattedDate}.xlsx`;

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        showToast.success("Subscribers exported successfully");
      }
    } catch (error) {
      console.error(error.response?.data || "Unknown error");
      showToast.error("An error occurred while exporting subscribers");
    } finally {
      setLoading(false);
    }
  };
  const handleSendSmsSubmit = async (sendSmsData) => {
    if (!sendSmsData.message) {
      showToast.warning("Please enter a message");
      return;
    }

    // ✅ Use same logic as handleEmailSubmit
    const filterParams = checkingAll
      ? hasAnyFilter()
        ? getCurrentFilterParams()
        : {}
      : {};

    try {
      const response = await axios.post(
        `${API_URL}subscription/send-sms-bulk?checkingAll=${checkingAll}`,
        {
          message: sendSmsData.message,
          checkingAll: checkingAll,
          selectedIds: checkingAll ? [] : selectedSubscriptions,
          filters: filterParams,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status == 200 || response.status == 201) {
        showToast.success(response.data.message || "Sms Send Successfully");
        setSelectedSubscriptions([]);
        fetchSubscriptions();
      } else {
        showToast.error("Failed to send SMS");
      }
    } catch (error) {
      console.log(error.response?.data || "Unknown error");
      showToast.error("An error occurred while sending sms");
    }
  };

  const handleSendPushSubmit = async (sendPushData) => {
    // ✅ Use same logic as handleEmailSubmit
    const filterParams = checkingAll
      ? hasAnyFilter()
        ? getCurrentFilterParams()
        : {}
      : {};

    try {
      const response = await axios.post(
        `${API_URL}subscription/send-subscription-push?checkingAll=${checkingAll}`,
        {
          title: sendPushData.title,
          message: sendPushData.message,
          checkingAll: checkingAll,
          selectedIds: checkingAll ? [] : selectedSubscriptions,
          filters: filterParams,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status == 200 || response.status == 201) {
        showToast.success(
          response.data.message || "Push Notification Send Successfully"
        );
        setSelectedSubscriptions([]);
        fetchSubscriptions();
      } else {
        showToast.error("Failed to send push notification");
      }
    } catch (error) {
      console.log(error.response?.data || "Unknown error");
      showToast.error("An error occurred while sending push notification");
    }
  };


  const tableContainerRef = useRef(null);
  const topScrollRef = useDoubleScroll(tableContainerRef);

  return (
    <div className="panel-content mt-5">
      {/* {loading && <LoadingSpinner />} */}
      {/* {(loading || isRendering) && <LoadingSpinner />} */}
      <div className="panel-heading d-flex justify-content-between">
        <div className="col-md-4">
          <div
            style={{ float: "left", position: "relative", marginLeft: "5px" }}
          >
            <select
              value="" // No value needed for action dropdown
              onChange={(e) => {
                const selectedAction = e.target.value;
                if (selectedAction) {
                  handleActionChange(selectedAction);
                }
              }}
              style={{
                width: "110px",
                padding: "0.25rem 0.5rem",
                fontSize: "0.875rem",
                lineHeight: "1.5",
                borderRadius: "0.2rem",
                border: "1px solid #ced4da",
                appearance: "none",
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 0.5rem center",
                backgroundSize: "16px 12px",
              }}
            >
              <option value="">Action</option>
              <PermissionWrapper module="subscription" action="delete">
                <option value="delete">Delete Subscription</option>
              </PermissionWrapper>
              <PermissionWrapper module="bulk_edit" action="view">
                <option value="bulkEdit">Bulk Edit</option>
              </PermissionWrapper>
              <PermissionWrapper module="Add_credits_bulk" action="view">
                <option value="addCredits">Add Credits Bulk</option>
              </PermissionWrapper>
              {/* <option value="bulkExport">Bulk Export Data</option>
              <option value="sendSms">Send SMS</option>
              <option value="sendPush">Send Push Notification</option>
              <option value="sendEmail">Send Email</option> */}
              <PermissionWrapper module="campaign_export" action="view">
                <option value="campaignExport">Campaign Export</option>
              </PermissionWrapper>
            </select>
            {(hasAnyFilter() || activeFilter || subscriberId) && (
              <button
                className="btn btn-default btn-xs ml-1"
                style={{ marginLeft: "6px" }}
                onClick={() => {
                  // Clear all filter states
                  setJoinDateFilter([null, null]);
                  setNameFilter("");
                  setMobileFilter("");
                  setLeadSourceFilter("");
                  setSubscriberCreatedDateFilter([null, null]);
                  setServiceFilter("");
                  setSubscriptionTypeFilter([]);
                  setServiceStatusFilter("");
                  setServiceTypeFilter("");
                  setPlanTypeFilter("");
                  setAmountFilter("");
                  setActualAmountFilter("");
                  setActivtionDateFilter([null, null]);
                  setExpiryDateFilter([null, null]);
                  setRenewDateFilter([null, null]);
                  setTotalCreditsFilter("");
                  setUsedCreditsFilter("");
                  setAvailableCreditsFilter("");
                  setSalesManagerFilter([]);
                  setAssignedUserFilter("");
                  setUpdatedByFilter("");
                  setUtmSourceFilter("");
                  setUtmMediumFilter("");
                  setUtmCampaignFilter("");
                  setUtmContentFilter("");
                  setUtmTermFilter("");
                  setSortField("");
                  setSortOrder("");
                  setPage(1);

                  // Clear URL parameters if needed
                  if (activeFilter || subscriberId) {
                    navigate("/SubscriptionPortfolio"); // Navigate to base URL without params
                  }

                  fetchSubscriptions(1);
                }}
                title="Clear all filters"
              >
                Clear Filter
              </button>
            )}
          </div>
        </div>
        <div>
          {/* <span style={{ fontSize: "0.8rem" }}>
            {start} to {end} of {serviceTypeCounts.service_type_1}
          </span> */}
          <span style={{ fontSize: "0.8rem" }}>
            {loading ? (
              <span
                className="skeleton-text"
                style={{ width: "140px", display: "inline-block" }}
              ></span>
            ) : (
              `${start} to ${end} of ${serviceTypeCounts?.service_type_1 ?? 0}`
            )}
          </span>
          <button
            className="btn btn-default btn-sm mx-1"
            onClick={handleRefresh}
          >
            <FontAwesomeIcon icon={faRefresh} />
          </button>
          {/* <button
            className="btn btn-default btn-sm mx-1"
            onClick={toggleModalFreeService}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Free Service
          </button>
          <button
            className="btn btn-default btn-sm mx-1"
            onClick={toggleModalPaidServiceOrder}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Paid Service Order
          </button> */}
          <button
            className={`btn btn-default btn-sm ${page === 1 ? "d-none" : ""}`}
            title="Previous"
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          &nbsp;
          <button
            className={`btn btn-default btn-sm ${page === totalPages ? "d-none" : ""
              }`}
            title="Next"
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>
      <div className="panel panel-default with-rounded-corner with-shadow">
        <div className="large-table-container-3">
          <div
            ref={topScrollRef}
            style={{
              overflowX: "auto",
              overflowY: "hidden",
              height: "16px",
              // width: "100%",
              marginBottom: "2px",
              background: "#f8f9fa",
            }}
          >
            {/* This inner div's width should match the table's width */}
            <div style={{ width: "100%", height: "1px" }}></div>
          </div>
          <div
            // style={{
            //   overflowY: "auto",
            //   maxHeight: "400px",
            //   overflowX: "auto",
            // }}
            ref={tableContainerRef}
            style={{
              overflowY: "auto",
              height: "calc(112vh - 250px)",
              overflowX: "auto",
              position: "relative",
            }}
          >
            <div className="d-flex justify-content-center">
              <button
                className={`btn ${checkingAll ? "btn-primary" : "btn-default"
                  } btn-xs ${showSelectAllButton || checkingAll ? "d-block" : "d-none"
                  }`}
                onClick={toggleCheckingAll}
              >
                {checkingAll
                  ? "All records are selected"
                  : "Select All the Records"}
              </button>
            </div>
            <table className="table table-condensed table-hover bg-white sticky-table">
              <thead>
                <tr>
                  <th>
                    {/* Add a checkbox for selecting all subscribers */}
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        // Handle select all functionality
                        if (e.target.checked) {
                          // Select all subscribers on the current page
                          const allIds = filteredUsers.map((user) => user.id);
                          setSelectedSubscriptions(allIds);
                          setCheckingAll(false); // Reset checkingAll when manually selecting
                        } else {
                          // Deselect all subscribers
                          setSelectedSubscriptions([]);
                          setCheckingAll(false); // Reset checkingAll when deselecting
                        }
                      }}
                      checked={
                        checkingAll ||
                        (filteredUsers.length > 0 &&
                          selectedSubscriptions.length === filteredUsers.length)
                      }
                    />
                  </th>
                  <th
                    style={{ minWidth: "155px", cursor: "pointer" }}
                    onClick={() => sortBox("created_on")}
                  >
                    Created Time{" "}
                    <FontAwesomeIcon icon={getSortIcon("created_on")} />
                  </th>
                  <FieldPermissionWrapper field="subscriber_fullname">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("fullname")}
                    >
                      Name <FontAwesomeIcon icon={getSortIcon("fullname")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscriber_mobileno">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("mobileno")}
                    >
                      Mobile No{" "}
                      <FontAwesomeIcon icon={getSortIcon("mobileno")} />
                    </th>
                  </FieldPermissionWrapper>
                  <th>Lead Source</th>
                  <FieldPermissionWrapper field="subscriber_created_date">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("subscriber_created_date")}
                    >
                      Subscriber Created Date{" "}
                      <FontAwesomeIcon
                        icon={getSortIcon("subscriber_created_date")}
                      />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_service">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("serviceid")}
                    >
                      Service{" "}
                      <FontAwesomeIcon icon={getSortIcon("serviceid")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_type">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("stype")}
                    >
                      Type <FontAwesomeIcon icon={getSortIcon("stype")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_status">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("status")}
                    >
                      Status <FontAwesomeIcon icon={getSortIcon("status")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_service_type">
                    <th>Service Type</th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_amount">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("amount")}
                    >
                      Amount <FontAwesomeIcon icon={getSortIcon("amount")} />
                    </th>
                  </FieldPermissionWrapper>
                  {/* <FieldPermissionWrapper field="subscription_amount">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("actual_amount")}
                    >
                      Actual Amount{" "}
                      <FontAwesomeIcon icon={getSortIcon("actual_amount")} />
                    </th>
                  </FieldPermissionWrapper> */}
                  <FieldPermissionWrapper field="subscription_activation_date">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("activation_date")}
                    >
                      Activation Date{" "}
                      <FontAwesomeIcon icon={getSortIcon("activation_date")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_expiry_date">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("expiry_date")}
                    >
                      Expiry Date{" "}
                      <FontAwesomeIcon icon={getSortIcon("expiry_date")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_renew_date">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("renew_date")}
                    >
                      Renew Date{" "}
                      <FontAwesomeIcon icon={getSortIcon("renew_date")} />
                    </th>
                  </FieldPermissionWrapper>
                  <th
                    style={{ minWidth: "155px", cursor: "pointer" }}
                    onClick={() => sortBox("trades_total")}
                  >
                    Duration{" "}
                    <FontAwesomeIcon icon={getSortIcon("trades_total")} />
                  </th>
                  {/* <th
                    style={{ minWidth: "155px", cursor: "pointer" }}
                    onClick={() => sortBox("trades_used")}
                  >
                    Total Used{" "}
                    <FontAwesomeIcon icon={getSortIcon("trades_used")} />
                  </th> */}
                  {/* <th
                    style={{ minWidth: "155px", cursor: "pointer" }}
                    onClick={() => sortBox("trades_available")}
                  >
                    Total Available{" "}
                    <FontAwesomeIcon icon={getSortIcon("trades_available")} />
                  </th> */}
                  <FieldPermissionWrapper field="subscription_sales_manager">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("sales_manager")}
                    >
                      Sales Manager{" "}
                      <FontAwesomeIcon icon={getSortIcon("sales_manager")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_assigned_user">
                    <th>Assigned User</th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_updated_by">
                    <th>Updated By </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_utmsource">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("utm_source")}
                    >
                      UTM Source{" "}
                      <FontAwesomeIcon icon={getSortIcon("utm_source")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_utmmedium">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("utm_medium")}
                    >
                      UTM Medium{" "}
                      <FontAwesomeIcon icon={getSortIcon("utm_medium")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_utmcampaign">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("utm_campaign")}
                    >
                      UTM Campaign{" "}
                      <FontAwesomeIcon icon={getSortIcon("utm_campaign")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_utmcontent">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("utm_content")}
                    >
                      UTM Content{" "}
                      <FontAwesomeIcon icon={getSortIcon("utm_content")} />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_utmterm">
                    <th
                      style={{ minWidth: "155px", cursor: "pointer" }}
                      onClick={() => sortBox("utm_term")}
                    >
                      UTM Term{" "}
                      <FontAwesomeIcon icon={getSortIcon("utm_term")} />
                    </th>
                  </FieldPermissionWrapper>
                </tr>
                <tr>
                  <th></th>
                  <th>
                    <div className="custom-datepicker-wrapper d-flex align-items-center gap-1">
                      <DatePicker
                        selected={joinDateFilter[0]}
                        onChange={handleJoinDateChange}
                        startDate={joinDateFilter[0]}
                        endDate={joinDateFilter[1]}
                        selectsRange
                        isClearable
                        toggleCalendarOnIconClick
                        placeholderText=""
                        monthsShown={3}
                        openToDate={joinDateFilter[0] || new Date()} // Open at selected date if exists, else prevMonthStart
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        scrollableYearDropdown
                        yearDropdownItemNumber={15} // Shows 15 years in dropdown
                        minDate={new Date(2018, 0, 1)} // Start from 2018
                        maxDate={new Date(2030, 11, 31)}
                        dateFormat="dd-MM-yyyy" // Display format
                      />
                      <button
                        type="button"
                        className="btn btn-default btn-xs btn-group-xs"
                        onClick={() => handleSearch()}
                      >
                        <FontAwesomeIcon icon={faSearch} />
                      </button>
                    </div>
                  </th>
                  <FieldPermissionWrapper field="subscriber_fullname">
                    <th>
                      <input
                        type="text"
                        placeholder=""
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        style={{
                          // width: "90px",
                          padding: "5px",
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSearch();
                        }}
                      />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscriber_mobileno">
                    <th>
                      <input
                        type="text"
                        placeholder=""
                        value={mobileFilter}
                        onChange={(e) => setMobileFilter(e.target.value)}
                        style={{
                          // width: "90px",
                          padding: "5px",
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSearch();
                        }}
                      />
                    </th>
                  </FieldPermissionWrapper>
                  <th>
                    <select
                      value={leadSourceFilter}
                      onChange={(e) => setLeadSourceFilter(e.target.value)}
                      style={{ width: "110px", padding: "5px" }}
                    >
                      <option value="">--select--</option>
                      {dropdownOptions.leadSourceList &&
                        dropdownOptions.leadSourceList.map((item, index) => (
                          <option key={index} value={item.pick_name}>
                            {item.pick_name}
                          </option>
                        ))}
                    </select>
                  </th>
                  <FieldPermissionWrapper field="subscriber_created_date">
                    <th>
                      <div className="d-flex align-items-center gap-1">
                        <DatePicker
                          selected={subscriberCreatedDateFilter[0]}
                          onChange={handleSubscriberDateChange}
                          startDate={subscriberCreatedDateFilter[0]}
                          endDate={subscriberCreatedDateFilter[1]}
                          selectsRange
                          isClearable
                          toggleCalendarOnIconClick
                          placeholderText=""
                          monthsShown={3}
                          openToDate={
                            subscriberCreatedDateFilter[0] || new Date()} // Open at selected date if exists, else prevMonthStart
                          showYearDropdown
                          showMonthDropdown
                          dropdownMode="select"
                          scrollableYearDropdown
                          yearDropdownItemNumber={15} // Shows 15 years in dropdown
                          minDate={new Date(2018, 0, 1)} // Start from 2018
                          maxDate={new Date(2030, 11, 31)}
                          dateFormat="dd-MM-yyyy" // Display format
                        />
                        <button
                          type="button"
                          className="btn btn-default btn-xs btn-group-xs"
                          onClick={() => handleSearch()}
                        >
                          <FontAwesomeIcon icon={faSearch} />
                        </button>
                      </div>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_service">
                    <th>
                      <select
                        value={serviceFilter}
                        onChange={(e) => setServiceFilter(e.target.value)}
                        style={{
                          // width: "110px",
                          padding: "5px",
                        }}
                      >
                        <option value="">--select--</option>
                        {dropdownOptions.serviceList &&
                          dropdownOptions.serviceList.map((item, index) => (
                            <option key={index} value={item.id}>
                              {item.service_name}
                            </option>
                          ))}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_type">
                    <th>
                      <MultiSelectDropdown
                        options={dropdownOptions.subscriptionTypeList || []}
                        selectedValues={subscriptionTypeFilter}
                        onChange={setSubscriptionTypeFilter}
                        placeholder="--Select--"
                        // searchPlaceholder="Search type..."
                        onSearch={() => handleSearch()}
                        className="subscription-type-dropdown"
                      />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_status">
                    <th>
                      <select
                        value={serviceStatusFilter}
                        onChange={(e) => setServiceStatusFilter(e.target.value)}
                        style={{
                          // width: "110px",
                          padding: "5px",
                        }}
                      >
                        <option value="">--select--</option>
                        {dropdownOptions.serviceStatusList &&
                          dropdownOptions.serviceStatusList.map(
                            (item, index) => (
                              <option key={index} value={item.pick_name}>
                                {item.pick_name}
                              </option>
                            )
                          )}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_service_type">
                    <th>
                      <select
                        value={serviceTypeFilter}
                        onChange={(e) => setServiceTypeFilter(e.target.value)}
                        style={{
                          //  width: "110px",
                          padding: "5px",
                        }}
                      >
                        <option value="">--select--</option>
                        {dropdownOptions.serviceTypeList &&
                          dropdownOptions.serviceTypeList.map((item, index) => (
                            <option key={index} value={item.pick_name}>
                              {item.pick_name}
                            </option>
                          ))}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_amount">
                    <th>
                      <input
                        type="text"
                        placeholder="e.g. 100-500"
                        title="Enter single value or range (e.g. 100-500, >=100, <=500)"
                        value={amountFilter}
                        onChange={(e) => setAmountFilter(e.target.value)}
                        style={{
                          // width: "90px",
                          padding: "5px",
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSearch();
                        }}
                      />
                    </th>
                  </FieldPermissionWrapper>
                  {/* <FieldPermissionWrapper field="subscription_amount">
                    <th>
                      <input
                        type="text"
                        placeholder=""
                        value={actualAmountFilter}
                        onChange={(e) => setActualAmountFilter(e.target.value)}
                        style={{
                          // width: "90px",
                          padding: "5px",
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSearch();
                        }}
                      />
                    </th>
                  </FieldPermissionWrapper> */}
                  <FieldPermissionWrapper field="subscription_activation_date">
                    <th>
                      <div className="d-flex align-items-center gap-1">
                        <DatePicker
                          selected={activtionDateFilter[0]}
                          onChange={handleActivationDateChange}
                          startDate={activtionDateFilter[0]}
                          endDate={activtionDateFilter[1]}
                          selectsRange
                          isClearable
                          toggleCalendarOnIconClick
                          placeholderText=""
                          monthsShown={3}
                          openToDate={activtionDateFilter[0] || new Date()} // Open at selected date if exists, else prevMonthStart
                          showYearDropdown
                          showMonthDropdown
                          dropdownMode="select"
                          scrollableYearDropdown
                          yearDropdownItemNumber={15} // Shows 15 years in dropdown
                          minDate={new Date(2018, 0, 1)} // Start from 2018
                          maxDate={new Date(2030, 11, 31)}
                          dateFormat="dd-MM-yyyy" // Display format
                        />
                        <button
                          type="button"
                          className="btn btn-default btn-xs btn-group-xs"
                          onClick={() => handleSearch()}
                        >
                          <FontAwesomeIcon icon={faSearch} />
                        </button>
                      </div>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_expiry_date">
                    <th>
                      <div className="d-flex align-items-center gap-1">
                        <DatePicker
                          selected={expiryDateFilter[0]}
                          onChange={handleExpiryDateChange}
                          startDate={expiryDateFilter[0]}
                          endDate={expiryDateFilter[1]}
                          selectsRange
                          isClearable
                          toggleCalendarOnIconClick
                          placeholderText=""
                          monthsShown={3}
                          openToDate={expiryDateFilter[0] || new Date()} // Open at selected date if exists, else prevMonthStart
                          showYearDropdown
                          showMonthDropdown
                          dropdownMode="select"
                          scrollableYearDropdown
                          yearDropdownItemNumber={15} // Shows 15 years in dropdown
                          minDate={new Date(2018, 0, 1)} // Start from 2018
                          maxDate={new Date(2030, 11, 31)}
                          dateFormat="dd-MM-yyyy" // Display format
                        />
                        <button
                          type="button"
                          className="btn btn-default btn-xs btn-group-xs"
                          onClick={() => handleSearch()}
                        >
                          <FontAwesomeIcon icon={faSearch} />
                        </button>
                      </div>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_renew_date">
                    <th>
                      <div className="d-flex align-items-center gap-1">
                        <DatePicker
                          selected={renewDateFilter[0]}
                          onChange={handleRenewDateChange}
                          startDate={renewDateFilter[0]}
                          endDate={renewDateFilter[1]}
                          selectsRange
                          isClearable
                          toggleCalendarOnIconClick
                          placeholderText=""
                          monthsShown={3}
                          openToDate={renewDateFilter[0] || prevMonthStart} // Open at selected date if exists, else prevMonthStart
                          showYearDropdown
                          showMonthDropdown
                          dropdownMode="select"
                          scrollableYearDropdown
                          yearDropdownItemNumber={15} // Shows 15 years in dropdown
                          minDate={new Date(2018, 0, 1)} // Start from 2018
                          maxDate={new Date(2030, 11, 31)}
                          dateFormat="dd-MM-yyyy" // Display format
                        />
                        <button
                          type="button"
                          className="btn btn-default btn-xs btn-group-xs"
                          onClick={() => handleSearch()}
                        >
                          <FontAwesomeIcon icon={faSearch} />
                        </button>
                      </div>
                    </th>
                  </FieldPermissionWrapper>
                  <th>
                    <select
                      value={totalCreditsFilter}
                      onChange={(e) => {
                        setTotalCreditsFilter(e.target.value);
                      }}
                      style={{
                        padding: "5px",
                      }}
                    >
                      <option value="">--select--</option>
                      <option value="1">1 Month</option>
                      <option value="3">3 Months</option>
                      <option value="6">6 Months</option>
                      <option value="12">12 Months</option>
                    </select>
                  </th>
                  {/* <th>
                    <input
                      type="text"
                      placeholder=""
                      value={usedCreditsFilter}
                      onChange={(e) => setUsedCreditsFilter(e.target.value)}
                      style={{
                        width: "90px",
                        padding: "5px",
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSearch();
                      }}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      placeholder=""
                      value={availableCreditsFilter}
                      onChange={(e) =>
                        setAvailableCreditsFilter(e.target.value)
                      }
                      style={{
                        width: "90px",
                        padding: "5px",
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSearch();
                      }}
                    />
                  </th> */}
                  <FieldPermissionWrapper field="subscription_sales_manager">
                    <th>
                      <MultiSelectDropdown
                        options={dropdownOptions.userList || []}
                        selectedValues={salesManagerFilter}
                        onChange={setSalesManagerFilter}
                        placeholder="--Select--"
                        // searchPlaceholder="Search sales manager..."
                        onSearch={() => handleSearch()}
                        className="sales-manager-dropdown"
                        valueKey="id"
                        displayKey="user_fullname"
                      />
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_assigned_user">
                    <th>
                      <select
                        value={assignedUserFilter}
                        onChange={(e) => setAssignedUserFilter(e.target.value)}
                        style={{
                          // width: "110px",
                          padding: "5px",
                        }}
                      >
                        <option value="">--select--</option>
                        {dropdownOptions.userList &&
                          dropdownOptions.userList.map((item, index) => (
                            <option key={index} value={item.id}>
                              {item.user_fullname}
                            </option>
                          ))}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_updated_by">
                    <th>
                      <select
                        value={updatedByFilter}
                        onChange={(e) => setUpdatedByFilter(e.target.value)}
                        style={{
                          // width: "110px",
                          padding: "5px",
                        }}
                      >
                        <option value="">--select--</option>
                        {dropdownOptions.fullUserList &&
                          dropdownOptions.fullUserList.map((item, index) => (
                            <option key={index} value={item.id}>
                              {item.user_fullname}
                            </option>
                          ))}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_utmsource">
                    <th>
                      <select
                        value={utmSourceFilter}
                        onChange={(e) => setUtmSourceFilter(e.target.value)}
                        style={{
                          // width: "110px",
                          padding: "5px",
                        }}
                      >
                        <option value="">--select--</option>
                        {dropdownOptions.utmSourceList &&
                          dropdownOptions.utmSourceList.map((item, index) => (
                            <option key={index} value={item}>
                              {item}
                            </option>
                          ))}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_utmmedium">
                    <th>
                      <select
                        value={utmMediumFilter}
                        onChange={(e) => setUtmMediumFilter(e.target.value)}
                        style={{
                          // width: "110px",
                          padding: "5px",
                        }}
                      >
                        <option value="">--select--</option>
                        {dropdownOptions.utmMediumList &&
                          dropdownOptions.utmMediumList.map((item, index) => (
                            <option key={index} value={item}>
                              {item}
                            </option>
                          ))}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_utmcampaign">
                    <th>
                      <select
                        value={utmCampaignFilter}
                        onChange={(e) => setUtmCampaignFilter(e.target.value)}
                        style={{
                          // width: "110px",
                          padding: "5px",
                        }}
                      >
                        <option value="">Select</option>
                        {dropdownOptions.utmCampaignList &&
                          dropdownOptions.utmCampaignList.map((item, index) => (
                            <option key={index} value={item}>
                              {item}
                            </option>
                          ))}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_utmcontent">
                    <th>
                      <select
                        value={utmContentFilter}
                        onChange={(e) => setUtmContentFilter(e.target.value)}
                        style={{
                          // width: "110px",
                          padding: "5px",
                        }}
                      >
                        <option value="">--select--</option>
                        {dropdownOptions.utmContentList &&
                          dropdownOptions.utmContentList.map((item, index) => (
                            <option key={index} value={item}>
                              {item}
                            </option>
                          ))}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                  <FieldPermissionWrapper field="subscription_utmterm">
                    <th>
                      <select
                        value={utmTermFilter}
                        onChange={(e) => setUtmTermFilter(e.target.value)}
                        style={{
                          // width: "110px",
                          padding: "5px",
                        }}
                      >
                        <option value="">--select--</option>
                        {dropdownOptions.utmTermList &&
                          dropdownOptions.utmTermList.map((item, index) => (
                            <option key={index} value={item}>
                              {item}
                            </option>
                          ))}
                      </select>
                    </th>
                  </FieldPermissionWrapper>
                </tr>
              </thead>
              <tbody>
                {(loading || isRendering) ? (
                  Array.from({ length: 10 }).map((_, skeletonIndex) => (
                    <tr
                      key={`skeleton-${skeletonIndex}`}
                      className="skeleton-row"
                    >
                      {/* Checkbox */}
                      <td>
                        <div className="skeleton-checkbox"></div>
                      </td>

                      {/* Created On */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "80%" }}
                        ></div>
                      </td>

                      {/* Full Name */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "90%" }}
                        ></div>
                      </td>

                      {/* Mobile */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "70%" }}
                        ></div>
                      </td>

                      {/* Lead Source */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "80%" }}
                        ></div>
                      </td>

                      {/* Subscriber Created */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "85%" }}
                        ></div>
                      </td>

                      {/* Service */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "90%" }}
                        ></div>
                      </td>

                      {/* Subscription Type */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "75%" }}
                        ></div>
                      </td>

                      {/* Status */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "80%" }}
                        ></div>
                      </td>

                      {/* Service Type */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "70%" }}
                        ></div>
                      </td>

                      {/* Amount */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "60%" }}
                        ></div>
                      </td>

                      {/* Activation Date */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "85%" }}
                        ></div>
                      </td>

                      {/* Expiry Date */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "85%" }}
                        ></div>
                      </td>

                      {/* Renew Date */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "85%" }}
                        ></div>
                      </td>

                      {/* Duration */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "70%" }}
                        ></div>
                      </td>

                      {/* Sales Manager */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "80%" }}
                        ></div>
                      </td>

                      {/* Assigned User */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "80%" }}
                        ></div>
                      </td>

                      {/* Updated By */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "75%" }}
                        ></div>
                      </td>

                      {/* UTM Source */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "70%" }}
                        ></div>
                      </td>

                      {/* UTM Medium */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "70%" }}
                        ></div>
                      </td>

                      {/* UTM Campaign */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "75%" }}
                        ></div>
                      </td>

                      {/* UTM Content */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "75%" }}
                        ></div>
                      </td>

                      {/* UTM Term */}
                      <td>
                        <div
                          className="skeleton-text"
                          style={{ width: "75%" }}
                        ></div>
                      </td>
                    </tr>
                  ))
                ) : (
                  filteredUsers.map((user) => (
                    <tr
                      key={user.id}
                      onClick={() => handleRowClick(user.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={
                            checkingAll ||
                            selectedSubscriptions.includes(user.id)
                          }
                          onChange={(e) =>
                            handleCheckboxChange(user?.id, e.target.checked)
                          }
                        />
                      </td>
                      <td>
                        {user?.created_on
                          ? moment(user?.created_on).format(
                            "DD-MM-YYYY hh:mm A"
                          )
                          : ""}
                      </td>
                      {/* <td>{user?.fullname || ""}</td> */}
                      <FieldPermissionWrapper field="subscriber_fullname">
                        <td
                          style={{ cursor: "pointer", color: "#0d6efd" }}
                          onMouseEnter={(e) =>
                          (e.currentTarget.style.textDecoration =
                            "underline")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.textDecoration = "none")
                          }
                        >
                          <a
                            href={`/Subscriber/Edit/${user?.subscriberid ||
                              user?.subscriberId ||
                              user?.subscriber_id ||
                              "#"
                              }`}
                            onClick={(e) => {
                              e.preventDefault(); // prevent default link navigation
                              e.stopPropagation();
                              const sid =
                                user?.subscriberid ||
                                user?.subscriberId ||
                                user?.subscriber_id;
                              if (sid) {
                                navigate(`/Subscriber/Edit/${sid}`);
                              } else {
                                showToast.warning(
                                  "Subscriber ID not found for this record"
                                );
                              }
                            }}
                            style={{
                              color: "#0d6efd",
                              textDecoration: "none",
                              display: "block",
                            }}
                          >
                            {user?.fullname || ""}
                            {/* Add investment icon conditionally */}
                            {user?.invest_status > 0 && (
                              <FontAwesomeIcon
                                icon={faChartLine}
                                className="text-success ms-1"
                                style={{ fontSize: "12px" }}
                                title="Has Investment"
                              />
                            )}
                          </a>
                        </td>
                      </FieldPermissionWrapper>
                      <FieldPermissionWrapper field="subscriber_mobileno">
                        <td>{user?.mobileno || ""}</td>
                      </FieldPermissionWrapper>
                      {/* <td>{user?.lead_source || ""}</td> */}
                      <td>
                        {dropdownOptions.leadSourceList.find(
                          (source) =>
                            source.id === parseInt(user?.lead_source)
                        )?.pick_name || user?.lead_source}
                      </td>
                      <FieldPermissionWrapper field="subscriber_created_date">
                        <td>
                          {user?.subscriber_created_on
                            ? moment(user?.subscriber_created_on).format(
                              "DD-MM-YYYY hh:mm A"
                            )
                            : ""}
                        </td>
                      </FieldPermissionWrapper>
                      <FieldPermissionWrapper field="subscription_service">
                        <td>{user?.service_name || ""}</td>
                      </FieldPermissionWrapper>
                      <FieldPermissionWrapper field="subscription_type">
                        {/* <td>{user?.stype || ""}</td> */}
                        <td>
                          {dropdownOptions.subscriptionTypeList.find(
                            (serviceType) =>
                              serviceType.id === parseInt(user?.stype)
                          )?.pick_name || user?.stype}
                        </td>
                      </FieldPermissionWrapper>

                      <FieldPermissionWrapper field="subscription_status">
                        {/* <td>{user?.status || ""}</td> */}
                        <td>
                          {dropdownOptions.serviceStatusList.find(
                            (serviceStatus) =>
                              serviceStatus.id === parseInt(user?.status)
                          )?.pick_name || user?.status}
                        </td>
                      </FieldPermissionWrapper>
                      <FieldPermissionWrapper field="subscription_service_type">
                        {/* <td>{user?.servicetype || ""}</td> */}
                        <td>
                          {dropdownOptions.serviceTypeList.find(
                            (serviceType) =>
                              serviceType.id === parseInt(user?.servicetype)
                          )?.pick_name || user?.servicetype}
                        </td>
                      </FieldPermissionWrapper>
                      <FieldPermissionWrapper field="subscription_amount">
                        <td>{user?.amount || ""}</td>
                      </FieldPermissionWrapper>
                      {/* <FieldPermissionWrapper field="subscription_amount">
                      <td>{user?.actual_amount || ""}</td>
                    </FieldPermissionWrapper> */}
                      <FieldPermissionWrapper field="subscription_activation_date">
                        <td>
                          {user?.activation_date
                            ? moment(user?.activation_date).format(
                              "DD-MM-YYYY hh:mm A"
                            )
                            : ""}
                        </td>
                      </FieldPermissionWrapper>

                      <FieldPermissionWrapper field="subscription_expiry_date">
                        <td>
                          {user?.expiry_date
                            ? moment(user?.expiry_date).format(
                              "DD-MM-YYYY hh:mm A"
                            )
                            : ""}
                        </td>
                      </FieldPermissionWrapper>

                      <FieldPermissionWrapper field="subscription_renew_date">
                        <td>
                          {user?.renew_date
                            ? moment(user?.renew_date).format(
                              "DD-MM-YYYY hh:mm A"
                            )
                            : ""}
                        </td>
                      </FieldPermissionWrapper>

                      {/* <td>{user?.trades_total}</td> */}
                      <td>
                        {user?.trades_total}{" "}
                        {user?.trades_total > 1 ? "Months" : "Month"}
                      </td>
                      {/* <td>{user?.trades_used}</td>
                    <td>{user?.trades_available}</td> */}
                      <FieldPermissionWrapper field="subscription_sales_manager">
                        <td>{user?.sales_manager || ""}</td>
                      </FieldPermissionWrapper>
                      <FieldPermissionWrapper field="subscription_assigned_user">
                        <td>{user?.assigned_user || ""}</td>
                      </FieldPermissionWrapper>
                      <FieldPermissionWrapper field="subscription_updated_by">
                        <td>{user?.updated_by || ""}</td>
                      </FieldPermissionWrapper>
                      <FieldPermissionWrapper field="subscription_utmsource">
                        <td>{user?.utm_source || ""}</td>
                      </FieldPermissionWrapper>
                      <FieldPermissionWrapper field="subscription_utmmedium">
                        <td>{user?.utm_medium || ""}</td>
                      </FieldPermissionWrapper>
                      <FieldPermissionWrapper field="subscription_utmcampaign">
                        <td>{user?.utm_campaign || ""}</td>
                      </FieldPermissionWrapper>
                      <FieldPermissionWrapper field="subscription_utmcontent">
                        <td>{user?.utm_content || ""}</td>
                      </FieldPermissionWrapper>
                      <FieldPermissionWrapper field="subscription_utmterm">
                        <td>{user?.utm_term || ""}</td>
                      </FieldPermissionWrapper>
                    </tr>
                  ))
                )}
              </tbody>
              {/* Add this footer section */}
              <tfoot>
                <tr style={{ backgroundColor: "#f8f9fa", fontWeight: "bold" }}>
                  {/* Amount column thi pehla badha columns */}
                  <td colSpan={10} style={{ textAlign: "right" }}>
                    Total Subscription Amount:
                  </td>

                  {/* ✅ Amount column */}
                  <td>
                    {loading ? (
                      <span
                        className="skeleton-text"
                        style={{ width: "100px", display: "inline-block" }}
                      ></span>
                    ) : (
                      totalSubscriptionAmount.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    )}
                  </td>

                  {/* Remaining columns after Amount */}
                  <td colSpan={12}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
      {/* Backdrop */}
      {(isModalOpenPaidServiceOrder ||
        isModalOpenFreeService ||
        isModalOpenBulkEdit ||
        isModalOpenCreditsAdd ||
        isModalOpenSendSms ||
        isModalOpenSendPush) && (
          <div
            className="modal-backdrop fade show"
            style={{
              backgroundColor: "rgba(0, 0, 0, 1)",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1040,
            }}
          ></div>
        )}

      {/* Modal structure */}
      {isModalOpenPaidServiceOrder && (
        <AddPaidServiceOrderPopup
          onClose={closeModal}
          dropdownOptions={dropdownOptions}
          fetchSubscriptions={fetchSubscriptions}
        />
      )}

      {isModalOpenFreeService && (
        <AddFreeServicePopup
          onClose={closeModal}
          dropdownOptions={dropdownOptions}
          fetchSubscriptions={fetchSubscriptions}
        />
      )}

      {isModalOpenBulkEdit && (
        <BulkEditPopup
          onClose={closeModal}
          dropdownOptions={dropdownOptions}
          fetchSubscriptions={fetchSubscriptions}
          selectedSubscriptions={selectedSubscriptions}
          onSubmit={handleBulkEditSubmit}
          isLoading={isLoading}
        />
      )}

      {isModalOpenCreditsAdd && (
        <AddCreditsPopup
          onClose={closeModal}
          dropdownOptions={dropdownOptions}
          fetchSubscriptions={fetchSubscriptions}
        />
      )}

      {isModalOpenSendSms && (
        <SendSmsBulkPopup
          onClose={closeModal}
          dropdownOptions={dropdownOptions}
          onSubmit={handleSendSmsSubmit}
        />
      )}

      {isModalOpenSendPush && (
        <SendPushNotificationPopup
          onClose={closeModal}
          dropdownOptions={dropdownOptions}
          onSubmit={handleSendPushSubmit}
        />
      )}
      {isModalOpenSendEmail && (
        <>
          {/* Backdrop */}
          <div
            className="modal-backdrop fade show"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1040,
            }}
          ></div>

          {/* Modal */}
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            style={{
              zIndex: 1050,
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "100%",
              maxWidth: "900px",
            }}
          >
            <div
              className="modal-dialog modal-lg modal-dialog-centered"
              role="document"
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Send Bulk Email</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setIsModalOpenSendEmail(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <form
                    onSubmit={handleEmailSubmit}
                    encType="multipart/form-data"
                  >
                    <div className="mb-3">
                      <label>Select Email Template</label>
                      <select
                        className="form-control"
                        value={emailData.emailtemp}
                        onChange={(e) => handleTemplateChange(e.target.value)}
                      >
                        <option value="">-- Select Template --</option>
                        {templateList.map((tpl) => (
                          <option key={tpl.id} value={tpl.id}>
                            {tpl.templatename}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="mb-3">
                      <label>Subject</label>
                      <input
                        type="text"
                        className="form-control"
                        value={emailData.subject}
                        onChange={(e) =>
                          setEmailData((prev) => ({
                            ...prev,
                            subject: e.target.value,
                          }))
                        }
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label>Message</label>
                      <ReactQuill
                        theme="snow"
                        value={emailData.message}
                        onChange={(value) =>
                          setEmailData((prev) => ({ ...prev, message: value }))
                        }
                      />
                    </div>

                    <div className="mb-3">
                      <label>Upload Attachment (Optional)</label>
                      <input
                        type="file"
                        className="form-control"
                        onChange={(e) =>
                          setEmailData({
                            ...emailData,
                            file: e.target.files[0],
                          })
                        }
                      />
                    </div>

                    <div className="d-flex justify-content-end">
                      <button type="submit" className="btn btn-primary me-2">
                        Send Email
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setIsModalOpenSendEmail(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Subscription;
